import * as React from 'react'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { requireAuth } from '@/lib/auth'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Search,X,RefreshCw } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { getPrintJob,getPrintJobs,getPrinters,getPrintAgents } from '@/features/devices/api'
import { useSelectedShopId } from '@/features/shared'
import { JobStatusBadge } from '@/features/devices/job-status-badge'
import { JobTypeBadge } from '@/features/devices/job-type-badge'
import { JobDetailCard } from '@/features/devices/job-detail-card'
import { formatDistanceToNow } from 'date-fns'
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from '@/components/ui/select'

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  limit: z.number().int().min(1).catch(20),
  search: z.string().catch(''),
  status: z.enum(['PENDING', 'SENT', 'PRINTED', 'FAILED', 'CANCELLED', '']).catch(''),
  type: z.enum(['RECEIPT', 'LABEL', 'EXIT_LABEL', 'PACKAGE_LABEL', 'RAW', '']).catch(''),
  printerId: z.string().catch(''),
})

let suppressedJobsAutoOpenSignature: string | null = null

export const Route = createFileRoute('/_dashboard/devices/jobs')({
  validateSearch: searchSchema,
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: JobsLayout,
})

function JobsLayout() {
  return <Outlet />
}

export function JobsPage({ selectedId }: { selectedId: string | undefined }) {
  const searchParams = useSearch({ from: Route.id })
  const navigate = useNavigate()
  const shopId = useSelectedShopId()

  const [searchTerm, setSearchTerm] = React.useState(searchParams.search || '')

  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const { page, limit, status, type, printerId, search } = searchParams
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${search}:${status}:${type}:${printerId}`

  const jobsQuery = useQuery({
    queryKey: queryKeys.printJobs(shopId ?? '', { page, limit, search, status, type, printerId }),
    queryFn: () =>
      getPrintJobs(shopId!, {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        printerId: printerId || undefined,
      }),
    enabled: !!shopId,
    refetchInterval: 5_000,
  })

  function handleRefresh() {
    setIsRefreshing(true)
    void jobsQuery.refetch().then(() => setTimeout(() => setIsRefreshing(false), 600))
  }

  const { data: printersData } = useQuery({
    queryKey: queryKeys.printers(shopId ?? '', { limit: 100 }),
    queryFn: () => getPrinters(shopId!, { limit: 100 }),
    enabled: !!shopId,
  })

  const printerMap = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const p of printersData?.data ?? []) {
      map.set(p.id, p.name)
    }
    return map
  }, [printersData])

  const { data: agentsData } = useQuery({
    queryKey: queryKeys.printAgents(shopId ?? '', { limit: 100 }),
    queryFn: () => getPrintAgents(shopId!, { limit: 100 }),
    enabled: !!shopId,
  })

  const agentMap = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const a of agentsData?.data ?? []) {
      map.set(a.id, a.name)
    }
    return map
  }, [agentsData])

  const jobs = React.useMemo(
    () => jobsQuery.data?.data ?? [],
    [jobsQuery.data?.data],
  )
  const isJobsLoading = jobsQuery.isLoading
  const meta = jobsQuery.data?.meta ?? { total: 0, page: 1, limit: 20, totalPages: 1 }
  const printers = printersData?.data ?? []

  const currentPageHasSelectedJob = React.useMemo(
    () => !!selectedId && jobs.some((job) => job.id === selectedId),
    [jobs, selectedId],
  )
  const effectiveSelectedId = React.useMemo(() => {
    if (currentPageHasSelectedJob) return selectedId
    if (isJobsLoading || jobs.length === 0) return undefined
    if (!selectedId && suppressedJobsAutoOpenSignature === pageSignature) return undefined
    return jobs[0].id
  }, [currentPageHasSelectedJob, isJobsLoading, jobs, pageSignature, selectedId])
  const listedJob = React.useMemo(
    () => jobs.find((j) => j.id === effectiveSelectedId),
    [jobs, effectiveSelectedId]
  )
  // The list omits each job's preview image (a large data URI); fetch it with
  // the job's detail only when a job is opened.
  const selectedJobDetail = useQuery({
    queryKey: queryKeys.printJob(effectiveSelectedId ?? ''),
    queryFn: () => getPrintJob(effectiveSelectedId!),
    enabled: !!listedJob,
    staleTime: 30_000,
  })
  const selectedJob = React.useMemo(
    () => (listedJob ? { ...listedJob, preview: selectedJobDetail.data?.preview ?? listedJob.preview } : undefined),
    [listedJob, selectedJobDetail.data]
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({
      to: '/devices/jobs',
      search: (prev) => ({ ...prev, page: 1, search: searchTerm || '' }),
    })
  }

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/devices/jobs/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }
      navigate({
        to: '/devices/jobs',
        search: (prev) => ({ ...prev }),
      })
    },
    [navigate]
  )

  const closeSelectedJob = React.useCallback(() => {
    suppressedJobsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/devices/jobs')) {
          suppressedJobsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const handleFilterChange = (key: 'status' | 'type' | 'printerId', value: string) => {
    navigate({
      to: '/devices/jobs',
      search: (prev) => ({ ...prev, page: 1, [key]: value === 'ALL' ? '' : value }),
    })
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Print Jobs</h2>
          <p className="text-muted-foreground">Monitor and manage recent print jobs across your devices.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-sm group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-8"
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearchTerm('')
                navigate({ to: '/devices/jobs', search: (prev) => ({ ...prev, page: 1, search: '' }) })
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </form>

        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={handleRefresh}>
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>

        <Select
          value={status || 'ALL'}
          onValueChange={(val) => handleFilterChange('status', val)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="SENT">Sent</SelectItem>
            <SelectItem value="PRINTED">Printed</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={type || 'ALL'}
          onValueChange={(val) => handleFilterChange('type', val)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Types</SelectItem>
            <SelectItem value="RECEIPT">Receipt</SelectItem>
            <SelectItem value="LABEL">Label</SelectItem>
            <SelectItem value="EXIT_LABEL">Exit Label</SelectItem>
            <SelectItem value="PACKAGE_LABEL">Package Label</SelectItem>
            <SelectItem value="RAW">Raw</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={printerId || 'ALL'}
          onValueChange={(val) => handleFilterChange('printerId', val)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Printers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Printers</SelectItem>
            {printers.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!isJobsLoading && jobs.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'}`}>
          {!isJobsLoading && jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No print jobs found</p>
              </div>
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-[200px]">ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Printer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isJobsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                  </TableRow>
                ))
              ) : (
                jobs.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer hover:bg-muted/50 data-[state=selected]:bg-muted"
                    data-state={effectiveSelectedId === job.id ? 'selected' : undefined}
                    onClick={() => effectiveSelectedId === job.id ? closeSelectedJob() : setSelectedId(job.id)}
                  >
                    <TableCell className="font-medium text-sm">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">#{job.id.slice(-5)}</span>
                          <JobStatusBadge status={job.status} dotOnly />
                        </div>
                        <span className="text-xs text-muted-foreground font-normal mt-0.5">
                          {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <JobTypeBadge jobType={job.jobType} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.printer?.name || printerMap.get(job.printerId) || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          )}
          {!isJobsLoading && jobs.length > 0 && (
            <DataTablePagination
              page={meta.page}
              itemsPerPage={meta.limit}
              totalItems={meta.total}
              totalPages={meta.totalPages}
              isLoading={isJobsLoading}
              entityName="jobs"
              onPageChange={(p) =>
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, page: p }),
                  replace: true,
                })
              }
              onLimitChange={(l) =>
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, page: 1, limit: l }),
                  replace: true,
                })
              }
            />
          )}
        </div>

        {selectedJob && (
          <JobDetailCard
            job={selectedJob}
            printerName={selectedJob.printer?.name || printerMap.get(selectedJob.printerId)}
            agentName={selectedJob.printAgent?.name || agentMap.get(selectedJob.agentId)}
            onClose={closeSelectedJob}
          />
        )}
      </div>
    </div>
  )
}
